import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus, faFileExport, faChartLine, faSpinner } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import ColumnVisibilityModal from '../../../components/ColumnVisibilityModal';
import { Link, useSearchParams } from 'react-router-dom';
import Table from '../../../components/Table';
import Pagination from '../../../components/Pagination';
import ReceitaFilterBar from '../../../components/ReceitaFilterBar';
import { fetchReceitas, fetchReceitasStats, fetchReceitasExport } from '../../../services/receitaService';

// Função para formatar data evitando problemas de fuso horário
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Se a data está no formato ISO com T00:00:00.000Z (midnight UTC)
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}T00:00:00\.000Z$/)) {
      // Para datas de balanço que são só data (sem hora específica), usar como data local
      const [datePart] = dateString.split('T');
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('pt-BR');
    }
    
    // Se a data está no formato ISO completo com hora específica
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)) {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    }
    
    // Se a data está no formato YYYY-MM-DD
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('pt-BR');
    }
    
    // Se a data está no formato YYYY-MM-DD HH:mm:ss
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
      const [datePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('pt-BR');
    }
    
    // Para outros formatos, tenta o comportamento padrão
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString; // Retorna a string original se não conseguir converter
    }
    return date.toLocaleDateString('pt-BR');
  } catch (error) {
    console.warn('Erro ao formatar data:', dateString, error);
    return dateString || '';
  }
};

// Função para formatar valores monetários no padrão brasileiro
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return 'R$ 0,00';
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value));
};

const columns = [
  { Header: 'ID', accessor: 'idEntrada' },
  { Header: 'Data Balanço', accessor: 'dataBalanco', Cell: (value) => formatDate(value) },
  { Header: 'Descrição', accessor: 'descricao' },
  { Header: 'Valor', accessor: 'valor', Cell: (value) => formatCurrency(value) },
  { Header: 'Conta', accessor: 'conta' },
  { Header: 'Pedido', accessor: 'pedido' },
  { Header: 'Quantidade', accessor: 'quantidade' },
  { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
  { Header: 'Data Cadastro', accessor: 'dataInclusao', Cell: (value) => formatDate(value) }
];

const pageSizes = [5, 10, 20, 50];

const ReceitasList = () => {
  // Permite chegar nesta tela com filtros pré-aplicados via querystring
  // (ex.: /financeiro/receitas?dataBalancoInicio=2026-04-26&dataBalancoFim=2026-05-26
  //  — drill-down do KPI "Receita líquida" do Dashboard Admin).
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    dataBalancoInicio: searchParams.get('dataBalancoInicio') || '',
    dataBalancoFim: searchParams.get('dataBalancoFim') || '',
    conta: searchParams.get('conta') || '',
    descricao: searchParams.get('descricao') || '',
    valorMin: searchParams.get('valorMin') || '',
    valorMax: searchParams.get('valorMax') || ''
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[1]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columns.map(col => col.accessor));
  const [receitas, setReceitas] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    page: 1,
    limit: 10
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportMode, setExportMode] = useState('detalhado'); // 'detalhado' | 'consolidado'
  const [error, setError] = useState(null);

  // Carregar dados
  useEffect(() => {
    const loadReceitas = async () => {
      try {
        setLoading(true);
        const [receitasResponse, statsData] = await Promise.all([
          fetchReceitas({ 
            page, 
            limit: pageSize,
            ...filters 
          }),
          fetchReceitasStats()
        ]);
        
        // Se receitasResponse é um objeto com data e pagination
        if (receitasResponse && typeof receitasResponse === 'object' && !Array.isArray(receitasResponse)) {
          const receitas = receitasResponse.entradas || receitasResponse.data || [];
          
          setReceitas(receitas);
          setPagination(receitasResponse.pagination || {
            total: 0,
            totalPages: 0,
            page: 1,
            limit: pageSize
          });
        } else {
          // Se é um array simples (fallback)
          setReceitas(Array.isArray(receitasResponse) ? receitasResponse : []);
          setPagination({
            total: Array.isArray(receitasResponse) ? receitasResponse.length : 0,
            totalPages: 1,
            page: 1,
            limit: pageSize
          });
        }
        
        setStats(statsData);
        setError(null);
      } catch (err) {
        setError('Erro ao carregar receitas');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadReceitas();
  }, [page, pageSize, filters]); // Adicionar filters nas dependências

  const handleFiltersChange = useCallback((e) => {
    setFilters(prevFilters => ({ ...prevFilters, [e.target.name]: e.target.value }));
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    const clearedFilters = {
      dataBalancoInicio: '', 
      dataBalancoFim: '', 
      conta: '', 
      descricao: '',
      valorMin: '',
      valorMax: ''
    };
    setFilters(clearedFilters);
    setPage(1);
  }, []);

  const handleColumnVisibilityChange = (columnAccessor) => {
    setVisibleColumns(prev => 
      prev.includes(columnAccessor) 
        ? prev.filter(col => col !== columnAccessor)
        : [...prev, columnAccessor]
    );
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const formatDateBalanco = (dataBalanco) => {
    if (!dataBalanco) return '';
    const [datePart] = String(dataBalanco).split('T');
    const [y, m, d] = datePart.split('-');
    return `${d}/${m}/${y}`;
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const dados = await fetchReceitasExport(filters);

      if (!dados || dados.length === 0) {
        alert('Nenhum dado encontrado para exportar com os filtros aplicados.');
        return;
      }

      // --- Cabeçalho do relatório ---
      const periodoTexto = filters.dataBalancoInicio || filters.dataBalancoFim
        ? `${filters.dataBalancoInicio || '...'} a ${filters.dataBalancoFim || '...'}`
        : 'TODOS OS PERÍODOS';
      const dataGeracao = new Date().toLocaleDateString('pt-BR');
      const modoLabel = exportMode === 'consolidado' ? 'CONSOLIDADO POR PEDIDO' : 'DETALHADO';

      let aoa, colWidths, numCols;

      // Ordenar por dataBalanco crescente (trata tanto string YYYY-MM-DD quanto Date object)
      const dadosOrdenados = [...dados].sort((a, b) => {
        const da = a.dataBalanco ? new Date(a.dataBalanco).getTime() : 0;
        const db = b.dataBalanco ? new Date(b.dataBalanco).getTime() : 0;
        return da - db;
      });

      // Colunas: DATA | EXTENSÃO | PEDIDO | VALOR | FORMA DE PAGAMENTO | ATENDENTE
      numCols = 6;
      const HEADERS = ['DATA', 'EXTENSÃO', 'PEDIDO', 'VALOR', 'FORMA DE PAGAMENTO', 'ATENDENTE'];
      const EMPTY_ROW = Array(numCols).fill('');

      if (exportMode === 'detalhado') {
        // ── MODO DETALHADO: uma linha por entrada financeira ──
        const linhas = dadosOrdenados.map(r => ([
          formatDateBalanco(r.dataBalanco),
          r.extensaoNome || '',
          r.pedido || '',
          Number(r.valor) || 0,
          r.formaPagamentoNome || '',
          r.cadastradoPor || '',
        ]));

        const totalValor = dadosOrdenados.reduce((acc, r) => acc + (Number(r.valor) || 0), 0);
        const totalFormatado = totalValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

        aoa = [
          [`RELATÓRIO DE RECEITAS FINANCEIRAS - ${modoLabel}`, ...EMPTY_ROW.slice(1)],
          [`PERÍODO: ${periodoTexto}`, ...EMPTY_ROW.slice(1)],
          [`Data de geração: ${dataGeracao}`, ...EMPTY_ROW.slice(1)],
          [],
          HEADERS,
          ...linhas,
          [],
          [`TOTAL DE RECEITAS: ${totalFormatado}`, ...EMPTY_ROW.slice(1)],
        ];

        colWidths = [
          { wch: 14 }, { wch: 35 }, { wch: 10 }, { wch: 18 }, { wch: 25 }, { wch: 20 },
        ];

      } else {
        // ── MODO CONSOLIDADO: agrupa por pedido, soma valores, ordena por data ──
        const grupos = {};
        dadosOrdenados.forEach(r => {
          const key = r.pedido != null ? String(r.pedido) : `sem-pedido-${r.idEntrada}`;
          if (!grupos[key]) {
            grupos[key] = {
              dataBalanco: r.dataBalanco,
              extensaoNome: r.extensaoNome || '',
              pedido: r.pedido || '',
              formaPagamentoNome: r.formaPagamentoNome || '',
              atendente: r.cadastradoPor || '',
              valor: 0,
            };
          }
          grupos[key].valor += Number(r.valor) || 0;
        });

        const linhas = Object.values(grupos).map(g => ([
          formatDateBalanco(g.dataBalanco),
          g.extensaoNome,
          g.pedido,
          g.valor,
          g.formaPagamentoNome,
          g.atendente,
        ]));

        const totalValor = linhas.reduce((acc, row) => acc + row[3], 0);
        const totalFormatado = totalValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

        aoa = [
          [`RELATÓRIO DE RECEITAS FINANCEIRAS - ${modoLabel}`, ...EMPTY_ROW.slice(1)],
          [`PERÍODO: ${periodoTexto}`, ...EMPTY_ROW.slice(1)],
          [`Data de geração: ${dataGeracao}`, ...EMPTY_ROW.slice(1)],
          [],
          HEADERS,
          ...linhas,
          [],
          [`TOTAL DE RECEITAS: ${totalFormatado}`, ...EMPTY_ROW.slice(1)],
        ];

        colWidths = [
          { wch: 14 }, { wch: 35 }, { wch: 10 }, { wch: 18 }, { wch: 25 }, { wch: 20 },
        ];
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = colWidths;
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } },
        { s: { r: aoa.length - 1, c: 0 }, e: { r: aoa.length - 1, c: numCols - 1 } },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Receitas Financeiras');
      const nomeArquivo = `Relatorio_Receitas_${exportMode}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, nomeArquivo);
    } catch (err) {
      console.error('Erro ao exportar:', err);
      alert('Erro ao exportar dados para Excel.');
    } finally {
      setExporting(false);
    }
  };

  // Filtrar colunas visíveis
  const visibleColumnsData = columns.filter(col => visibleColumns.includes(col.accessor));

  // Usar dados diretamente do servidor (sem filtro local)
  const tableData = receitas.map(receita => ({
    ...receita,
    actions: (
      <div className="flex space-x-2">
        <Link
          to={`/financeiro/receitas/${receita.idEntrada}`}
          className="text-blue-600 hover:text-blue-800 transition-colors"
          title="Visualizar"
        >
          <FontAwesomeIcon icon={faEye} />
        </Link>
        <Link
          to={`/financeiro/receitas/${receita.idEntrada}/edit`}
          className="text-green-600 hover:text-green-800 transition-colors"
          title="Editar"
        >
          <FontAwesomeIcon icon={faPen} />
        </Link>
      </div>
    )
  }));

  // Adicionar coluna de ações se não existir
  const columnsWithActions = visibleColumnsData.some(col => col.accessor === 'actions') 
    ? visibleColumnsData 
    : [...visibleColumnsData, { Header: 'Ações', accessor: 'actions' }];

  if (loading) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Receitas Financeiras</h2>
            <Link
              to="/financeiro/receitas/create"
              className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Nova Receita</span>
            </Link>
          </div>

          {/* Cards de Estatísticas */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-100 text-green-600">
                    <FontAwesomeIcon icon={faChartLine} size="lg" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total de Receitas</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(stats.total)}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                    <FontAwesomeIcon icon={faFileExport} size="lg" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Quantidade de Lançamentos</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.count}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                    <FontAwesomeIcon icon={faChartLine} size="lg" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Valor Médio</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatCurrency(stats.average)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <ColumnVisibilityModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            columns={columns}
            visibleColumns={visibleColumns}
            onColumnVisibilityChange={handleColumnVisibilityChange}
          />

          <div className="max-w-full">
            <ReceitaFilterBar 
              filters={filters}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
            />
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto flex items-center gap-4">
              <Pagination 
                currentPage={page} 
                totalPages={pagination.totalPages} 
                onPageChange={handlePageChange} 
              />
              <span className="text-sm text-gray-600">
                Total: {pagination.total} registros
              </span>
            </div>
            <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {pageSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <select
                value={exportMode}
                onChange={(e) => setExportMode(e.target.value)}
                disabled={exporting}
                className="px-2 py-1 rounded border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                title="Modo de exportação"
              >
                <option value="detalhado">Detalhado</option>
                <option value="consolidado">Consolidado por Pedido</option>
              </select>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-blue-600 text-white font-medium shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
                onClick={handleExportExcel}
                disabled={exporting}
                aria-label="Exportar para Excel"
              >
                <FontAwesomeIcon icon={exporting ? faSpinner : faFileExport} spin={exporting} />
                <span>{exporting ? 'Exportando...' : 'Exportar Excel'}</span>
              </button>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={() => setIsModalOpen(true)}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>

          <div className="max-w-full">
            <Table
              columns={columnsWithActions.filter(col => col.accessor === 'actions' || visibleColumns.includes(col.accessor))}
              data={tableData}
            />
          </div>

          {(!Array.isArray(receitas) || receitas.length === 0) && !loading && (
            <div className="text-center py-8 text-gray-500">
              Nenhuma receita encontrada.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceitasList;
